import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Plus, Trash2, Target, CalendarClock, ArrowRight, X, Pencil, CheckCircle2, ClipboardList, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { SPECIES } from "@/lib/hunterOptions";
import { STATE_ABBR } from "@/lib/stateMeta";

const STATES = Object.keys(STATE_ABBR).sort();
const POINT_TYPES = ["preference", "bonus", "weighted", "none"];
const STATUS = [
  ["planning", "Planning", "bg-white/10 text-bone/60"],
  ["upcoming", "Upcoming", "bg-blaze/15 text-blaze"],
  ["applied", "Applied", "bg-blaze/20 text-blaze"],
  ["pending", "Pending result", "bg-saddle/20 text-saddle"],
  ["successful", "Drawn", "bg-forest/30 text-forest"],
  ["unsuccessful", "Not drawn", "bg-red-500/15 text-red-300"],
  ["points_only", "Points only", "bg-white/10 text-bone/60"],
  ["skipped", "Skipped", "bg-white/5 text-bone/40"],
];
const STATUS_MAP = Object.fromEntries(STATUS.map(([v, l, c]) => [v, { l, c }]));
const overline = "hr-overline text-blaze text-[11px]";
const inputCls = "w-full bg-white/5 border border-white/15 rounded px-3 py-2.5 outline-none focus:border-blaze text-bone text-sm placeholder:text-bone/30";
const EMPTY_APP = { state: "", species: "", year: new Date().getFullYear(), application_type: "draw", unit_code: "", point_type: "preference", deadline: "", status: "planning", points_used: "", notes: "" };

function countdownLabel(days) {
  if (days === null || days === undefined) return null;
  if (days < 0) return { text: `Closed ${Math.abs(days)}d ago`, cls: "text-bone/40" };
  if (days === 0) return { text: "Due today", cls: "text-red-400" };
  if (days <= 14) return { text: `in ${days} days`, cls: "text-red-300" };
  if (days <= 45) return { text: `in ${days} days`, cls: "text-saddle" };
  return { text: `in ${days} days`, cls: "text-bone/50" };
}

export default function Applications() {
  const { isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [points, setPoints] = useState([]);
  const [showAppForm, setShowAppForm] = useState(false);
  const [appForm, setAppForm] = useState(EMPTY_APP);
  const [editId, setEditId] = useState(null);
  const [ptForm, setPtForm] = useState({ state: "", species: "", point_type: "preference", points: "", year: new Date().getFullYear() });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.applications(), api.myPoints()]).then(([a, p]) => { setApps(a.applications); setPoints(p || []); });
  useEffect(() => { if (isAuthed) load(); }, [isAuthed]);

  const openEdit = (a) => { setEditId(a.id); setAppForm({ ...EMPTY_APP, ...a, points_used: a.points_used ?? "" }); setShowAppForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const resetForm = () => { setShowAppForm(false); setEditId(null); setAppForm(EMPTY_APP); };

  const saveApp = async () => {
    if (!appForm.state || !appForm.species) { toast.error("Pick a state and species."); return; }
    setBusy(true);
    try {
      const body = { ...appForm, year: appForm.year ? Number(appForm.year) : null, points_used: appForm.points_used === "" ? null : Number(appForm.points_used) };
      if (editId) await api.updateApplication(editId, body); else await api.createApplication(body);
      toast.success(editId ? "Application updated." : "Application added.");
      resetForm(); await load();
    } catch { toast.error("Couldn't save."); }
    setBusy(false);
  };

  const setStatus = async (a, status) => { await api.updateApplication(a.id, { status }); await load(); };
  const delApp = async (id) => { await api.deleteApplication(id); await load(); };
  const convert = async (a) => {
    const r = await api.convertApplicationToHunt(a.id);
    toast.success(r.created ? "Planned hunt created from your draw." : "Opening your linked hunt.");
    navigate(`/hunts/${r.hunt.id}`);
  };

  const savePoint = async () => {
    if (!ptForm.state || !ptForm.species || ptForm.points === "") { toast.error("State, species and points required."); return; }
    await api.savePoints({ state: ptForm.state, species: ptForm.species, point_type: ptForm.point_type, points: Number(ptForm.points), year: Number(ptForm.year) });
    setPtForm({ ...ptForm, species: "", points: "" });
    toast.success("Points saved.");
    await load();
  };
  const delPoint = async (id) => { await api.deletePoints(id); await load(); };

  const grouped = useMemo(() => {
    const g = {};
    points.forEach((p) => { (g[p.state] = g[p.state] || []).push(p); });
    return Object.entries(g).sort();
  }, [points]);

  if (!ready || (isAuthed && !apps)) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }
  if (!isAuthed) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="max-w-lg mx-auto text-center py-24 px-5"><ClipboardList className="text-blaze mx-auto" /><h1 className="font-serif text-4xl text-bone mt-5">Applications & Points</h1><p className="text-bone/60 mt-3">Sign in to track your draws and points.</p></div></div>;
  }

  return (
    <div className="bg-charcoal min-h-screen grain text-bone" data-testid="applications-page">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-10">
        <span className={overline}>Plan · Apply</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2">Applications & Points</h1>
        <p className="text-bone/50 mt-2">Track every draw and point balance. Dates you enter are yours — HomeRange never invents deadlines or results.</p>

        {/* Applications */}
        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-serif text-2xl">Applications</h2>
          <button onClick={() => (showAppForm ? resetForm() : setShowAppForm(true))} className="inline-flex items-center gap-2 bg-blaze text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="add-application-btn">
            {showAppForm ? <><X size={15} /> Close</> : <><Plus size={15} /> Add application</>}
          </button>
        </div>

        <AnimatePresence>
          {showAppForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 mt-4" data-testid="application-form">
                <div className="grid sm:grid-cols-3 gap-3">
                  <select value={appForm.state} onChange={(e) => setAppForm({ ...appForm, state: e.target.value })} className={inputCls} data-testid="app-state"><option value="" className="bg-charcoal">State…</option>{STATES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}</select>
                  <input list="app-species" value={appForm.species} onChange={(e) => setAppForm({ ...appForm, species: e.target.value })} placeholder="Species" className={inputCls} data-testid="app-species" /><datalist id="app-species">{SPECIES.map((s) => <option key={s} value={s} />)}</datalist>
                  <input type="number" value={appForm.year} onChange={(e) => setAppForm({ ...appForm, year: e.target.value })} placeholder="Year" className={inputCls} data-testid="app-year" />
                  <input value={appForm.application_type} onChange={(e) => setAppForm({ ...appForm, application_type: e.target.value })} placeholder="Type (draw / OTC / points-only)" className={inputCls} data-testid="app-type" />
                  <input value={appForm.unit_code} onChange={(e) => setAppForm({ ...appForm, unit_code: e.target.value })} placeholder="Unit / hunt code" className={inputCls} data-testid="app-unit" />
                  <select value={appForm.point_type} onChange={(e) => setAppForm({ ...appForm, point_type: e.target.value })} className={inputCls} data-testid="app-point-type">{POINT_TYPES.map((p) => <option key={p} value={p} className="bg-charcoal">{p} points</option>)}</select>
                  <label className="text-xs text-bone/50">Application deadline<input type="date" value={appForm.deadline || ""} onChange={(e) => setAppForm({ ...appForm, deadline: e.target.value })} className={inputCls} data-testid="app-deadline" /></label>
                  <select value={appForm.status} onChange={(e) => setAppForm({ ...appForm, status: e.target.value })} className={inputCls} data-testid="app-status">{STATUS.map(([v, l]) => <option key={v} value={v} className="bg-charcoal">{l}</option>)}</select>
                  <input type="number" value={appForm.points_used} onChange={(e) => setAppForm({ ...appForm, points_used: e.target.value })} placeholder="Points used" className={inputCls} data-testid="app-points-used" />
                </div>
                <input value={appForm.notes} onChange={(e) => setAppForm({ ...appForm, notes: e.target.value })} placeholder="Notes (optional)" className={`${inputCls} mt-3`} data-testid="app-notes" />
                <button onClick={saveApp} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="app-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {editId ? "Update" : "Add"} application</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 space-y-3" data-testid="applications-list">
          {apps.length === 0 && <p className="text-bone/40 text-sm py-6 text-center border border-dashed border-white/10 rounded-xl">No applications yet. Add your first draw above.</p>}
          {apps.map((a) => {
            const cd = countdownLabel(a.days_to_deadline);
            const st = STATUS_MAP[a.status] || STATUS_MAP.planning;
            return (
              <div key={a.id} className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`application-${a.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-bone">{a.year} {a.state} {a.species}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                  </div>
                  <p className="text-bone/50 text-xs mt-1">{[a.unit_code, a.application_type, a.point_type && `${a.point_type} pts`, a.points_used != null && `${a.points_used} used`].filter(Boolean).join(" · ")}</p>
                </div>
                {a.deadline && cd && (
                  <div className="text-right" data-testid={`app-deadline-${a.id}`}>
                    <div className={`text-sm font-semibold flex items-center gap-1.5 ${cd.cls}`}><CalendarClock size={14} /> {cd.text}</div>
                    <div className="text-bone/30 text-[10px]">deadline you entered</div>
                  </div>
                )}
                <select value={a.status} onChange={(e) => setStatus(a, e.target.value)} className="bg-white/5 border border-white/15 rounded px-2 py-1.5 text-xs text-bone outline-none" data-testid={`app-quickstatus-${a.id}`}>{STATUS.map(([v, l]) => <option key={v} value={v} className="bg-charcoal">{l}</option>)}</select>
                {a.status === "successful" && (
                  a.hunt_id
                    ? <Link to={`/hunts/${a.hunt_id}`} className="inline-flex items-center gap-1 text-forest text-sm font-semibold" data-testid={`app-openhunt-${a.id}`}>Open hunt <ArrowRight size={14} /></Link>
                    : <button onClick={() => convert(a)} className="inline-flex items-center gap-1 bg-forest/20 text-forest px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-forest/30 transition-colors" data-testid={`app-convert-${a.id}`}><CheckCircle2 size={13} /> Plan this hunt</button>
                )}
                <button onClick={() => openEdit(a)} className="text-bone/40 hover:text-bone" data-testid={`app-edit-${a.id}`}><Pencil size={15} /></button>
                <button onClick={() => delApp(a.id)} className="text-bone/40 hover:text-red-400" data-testid={`app-delete-${a.id}`}><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>

        {/* Point Wallet */}
        <div id="points" className="mt-14">
          <h2 className="font-serif text-2xl flex items-center gap-2"><Target size={20} className="text-blaze" /> Point Wallet</h2>
          <p className="text-bone/50 mt-1 text-sm">States use different systems — preference, bonus, weighted, or none. Track each separately.</p>
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 mt-4">
            <div className="grid sm:grid-cols-5 gap-3">
              <select value={ptForm.state} onChange={(e) => setPtForm({ ...ptForm, state: e.target.value })} className={inputCls} data-testid="pt-state"><option value="" className="bg-charcoal">State…</option>{STATES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}</select>
              <input list="pt-species" value={ptForm.species} onChange={(e) => setPtForm({ ...ptForm, species: e.target.value })} placeholder="Species" className={inputCls} data-testid="pt-species" /><datalist id="pt-species">{SPECIES.map((s) => <option key={s} value={s} />)}</datalist>
              <select value={ptForm.point_type} onChange={(e) => setPtForm({ ...ptForm, point_type: e.target.value })} className={inputCls} data-testid="pt-type">{POINT_TYPES.map((p) => <option key={p} value={p} className="bg-charcoal">{p}</option>)}</select>
              <input type="number" value={ptForm.points} onChange={(e) => setPtForm({ ...ptForm, points: e.target.value })} placeholder="Points" className={inputCls} data-testid="pt-points" />
              <button onClick={savePoint} className="bg-blaze text-white rounded px-4 py-2.5 text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="pt-save">Save</button>
            </div>
          </div>
          <div className="mt-4 space-y-4" data-testid="points-list">
            {grouped.length === 0 && <p className="text-bone/40 text-sm py-4">No points saved yet.</p>}
            {grouped.map(([state, list]) => (
              <div key={state}>
                <span className="text-bone/40 text-xs uppercase tracking-wider">{state}</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {list.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-4 pr-2 py-2 text-sm" data-testid={`point-${p.id}`}>
                      <span className="text-bone/70">{p.species}</span>
                      <span className="font-serif text-lg text-bone">{p.points}</span>
                      <span className="text-bone/30 text-[10px]">{p.point_type || "preference"}</span>
                      <button onClick={() => delPoint(p.id)} className="text-bone/40 hover:text-red-400 ml-1" data-testid={`point-delete-${p.id}`}><X size={13} /></button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-bone/40 text-xs mt-10 flex items-center gap-1.5"><Lock size={11} /> Your applications and points are private to your HomeRange account.</p>
      </main>
      <Footer />
    </div>
  );
}
